/**
 * Admin Tool Workstation — a unified workbench of every platform tool.
 *
 * - Grid view: cards for each tool with status + tier badges, category filter,
 *   status filter, and search.
 * - Multi-pane workspace: click a card to open the tool in a right-side
 *   workspace (built-in components render inline, external routes open in an
 *   iframe pane). Multiple tools can be open in tabs.
 * - R&D bench: admins can inline-edit each tool's status (Concept → WIP →
 *   Beta → Live) and tier (Free / Pro / Admin).
 * - Create new tool: opens a form that lets admins register a new tool and
 *   choose the tier it lives under, plus its route or component key.
 *
 * Only admins can access this page (routing enforced in App.js).
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Plus, X, ExternalLink, Wrench, Search, ChevronLeft, Layers, Sparkles,
  Pencil, Trash2, LayoutGrid, PanelRightOpen, MessageCircle,
} from 'lucide-react';
import LabelMaker from '../../components/tools/LabelMaker';
import FoodPrepResource from '../../components/tools/FoodPrepResource';
import ToolFeedbackDrawer from '../../components/admin/tools/ToolFeedbackDrawer';

// ---- Inline component registry ---------------------------------------------
// Tools whose `component` field matches these keys render inline in the
// workspace pane rather than opening in an iframe.
const INLINE_COMPONENTS = {
  LabelMaker,
  FoodPrepResource,
};

const STATUS_META = {
  concept: { label: 'Concept', color: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
  wip:     { label: 'WIP',     color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  beta:    { label: 'Beta',    color: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200' },
  live:    { label: 'Live',    color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
};

const TIER_META = {
  free:  { label: 'Free',  color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200' },
  pro:   { label: 'Pro',   color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200' },
  admin: { label: 'Admin', color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200' },
};

const ALL_STATUSES = ['concept', 'wip', 'beta', 'live'];
const ALL_TIERS = ['free', 'pro', 'admin'];

// -------------------------------------------------------------------- ToolCard
function ToolCard({ tool, onOpen, onEdit, onDelete, onQuickStatus, onOpenFeedback }) {
  const s = STATUS_META[tool.status] || STATUS_META.concept;
  const t = TIER_META[tool.tier] || TIER_META.free;
  const unresolved = tool.unresolved_feedback || 0;
  const totalFeedback = tool.feedback_count || 0;
  return (
    <Card
      className="hover:border-primary/60 transition-colors cursor-pointer group"
      data-testid={`tool-card-${tool.slug}`}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              {tool.category}
            </div>
            <div className="font-bold text-base leading-tight mt-0.5 truncate">{tool.name}</div>
          </div>
          <div className="flex gap-1 shrink-0">
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${s.color}`}>{s.label}</span>
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${t.color}`}>{t.label}</span>
          </div>
        </div>
        <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
          {tool.description || '—'}
        </p>
        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              onClick={() => onOpen(tool)}
              data-testid={`tool-open-${tool.slug}`}
              disabled={tool.status === 'concept' && !tool.component && !tool.route}
            >
              <PanelRightOpen className="w-3.5 h-3.5 mr-1.5" /> Open
            </Button>
            <Button
              size="sm"
              variant={unresolved > 0 ? 'default' : 'outline'}
              className={`h-8 px-2 ${unresolved > 0 ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
              onClick={(e) => { e.stopPropagation(); onOpenFeedback(tool); }}
              data-testid={`tool-feedback-${tool.slug}`}
              title={`${totalFeedback} note${totalFeedback === 1 ? '' : 's'} · ${unresolved} unresolved`}
            >
              <MessageCircle className="w-3.5 h-3.5 mr-1" />
              <span className="text-[11px] font-bold">
                {totalFeedback}{unresolved > 0 ? ` · ${unresolved} open` : ''}
              </span>
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <select
              value={tool.status}
              onChange={(e) => onQuickStatus(tool, 'status', e.target.value)}
              className="text-[11px] h-7 px-1 rounded border border-input bg-background"
              onClick={(e) => e.stopPropagation()}
              data-testid={`tool-status-${tool.slug}`}
            >
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_META[s].label}</option>
              ))}
            </select>
            <select
              value={tool.tier}
              onChange={(e) => onQuickStatus(tool, 'tier', e.target.value)}
              className="text-[11px] h-7 px-1 rounded border border-input bg-background"
              onClick={(e) => e.stopPropagation()}
              data-testid={`tool-tier-${tool.slug}`}
            >
              {ALL_TIERS.map((t) => (
                <option key={t} value={t}>{TIER_META[t].label}</option>
              ))}
            </select>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={(e) => { e.stopPropagation(); onEdit(tool); }}
              data-testid={`tool-edit-${tool.slug}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            {!tool.built_in ? (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-rose-600"
                onClick={(e) => { e.stopPropagation(); onDelete(tool); }}
                data-testid={`tool-delete-${tool.slug}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// -------------------------------------------------------------------- ToolForm
function ToolForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(() => ({
    slug: '',
    name: '',
    description: '',
    category: 'General',
    icon: 'wrench',
    route: '',
    component: '',
    tier: 'admin',
    status: 'concept',
    owner_notes: '',
    ...initial,
  }));
  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <Card data-testid="tool-form">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">
          {initial?.id ? 'Edit tool' : 'Register new tool'}
        </CardTitle>
        <Button variant="ghost" size="icon" onClick={onCancel} data-testid="tool-form-close">
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Slug (unique)</Label>
            <Input
              value={form.slug}
              onChange={(e) => setField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="my-new-tool"
              disabled={!!initial?.built_in}
              data-testid="tf-slug"
            />
          </div>
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="My New Tool"
              data-testid="tf-name"
            />
          </div>
        </div>

        <div>
          <Label>Description</Label>
          <Textarea
            rows={2}
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            data-testid="tf-description"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Category</Label>
            <Input
              value={form.category}
              onChange={(e) => setField('category', e.target.value)}
              placeholder="Calculators / Builders / Admin / AI"
              data-testid="tf-category"
            />
          </div>
          <div>
            <Label>Tier</Label>
            <select
              value={form.tier}
              onChange={(e) => setField('tier', e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              data-testid="tf-tier"
            >
              {ALL_TIERS.map((t) => (
                <option key={t} value={t}>{TIER_META[t].label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Status</Label>
            <select
              value={form.status}
              onChange={(e) => setField('status', e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              data-testid="tf-status"
            >
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_META[s].label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Route (existing app path — optional)</Label>
            <Input
              value={form.route || ''}
              onChange={(e) => setField('route', e.target.value)}
              placeholder="/admin/portal or /my-tool"
              data-testid="tf-route"
            />
          </div>
          <div>
            <Label>Inline component key (optional)</Label>
            <Input
              value={form.component || ''}
              onChange={(e) => setField('component', e.target.value)}
              placeholder="LabelMaker"
              data-testid="tf-component"
            />
          </div>
        </div>

        <div>
          <Label>Owner notes</Label>
          <Textarea
            rows={2}
            value={form.owner_notes}
            onChange={(e) => setField('owner_notes', e.target.value)}
            placeholder="Design constraints, todo, target users…"
            data-testid="tf-notes"
          />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            onClick={() => onSave(form)}
            disabled={!form.slug.trim() || !form.name.trim()}
            data-testid="tf-save"
          >
            {initial?.id ? 'Save changes' : 'Create tool'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// -------------------------------------------------------------------- Workspace
function ToolWorkspace({ openTools, activeId, onSelect, onClose }) {
  const active = openTools.find((t) => t.id === activeId);
  if (openTools.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-10 text-slate-500 dark:text-slate-400">
        <Layers className="w-10 h-10 mb-3 opacity-40" />
        <div className="text-sm">
          Pick a tool on the left to open it in the workspace.
          <br />Open as many as you need &mdash; they&apos;ll stack as tabs.
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex items-center border-b border-border overflow-x-auto shrink-0">
        {openTools.map((t) => (
          <div
            key={t.id}
            className={`flex items-center gap-2 px-3 py-2 text-xs border-r border-border cursor-pointer whitespace-nowrap ${
              t.id === activeId ? 'bg-background font-bold' : 'bg-muted/40 hover:bg-muted'
            }`}
            onClick={() => onSelect(t.id)}
            data-testid={`ws-tab-${t.slug}`}
          >
            <Wrench className="w-3 h-3" />
            {t.name}
            <button
              onClick={(e) => { e.stopPropagation(); onClose(t.id); }}
              className="ml-1 text-slate-400 hover:text-rose-600"
              data-testid={`ws-close-${t.slug}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Active tool body */}
      <div className="flex-1 overflow-auto p-4">
        {active ? <ToolBody tool={active} /> : null}
      </div>
    </div>
  );
}

function ToolBody({ tool }) {
  // Prefer inline component
  if (tool.component && INLINE_COMPONENTS[tool.component]) {
    const Comp = INLINE_COMPONENTS[tool.component];
    return <Comp embedded />;
  }
  // Concept / WIP without route or component → rich placeholder
  if (!tool.route && !tool.component) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 text-slate-500 dark:text-slate-400">
        <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-60" />
        <div className="text-lg font-bold mb-1">{tool.name}</div>
        <div className="text-sm mb-3">
          This tool is in <span className="font-semibold">{STATUS_META[tool.status]?.label}</span>. No route or
          component wired up yet &mdash; set one in the tool&apos;s edit panel to launch it here.
        </div>
        {tool.owner_notes ? (
          <div className="text-xs bg-muted/40 rounded p-3 text-left whitespace-pre-wrap">
            <div className="font-bold text-[10px] uppercase tracking-widest mb-1">Owner notes</div>
            {tool.owner_notes}
          </div>
        ) : null}
      </div>
    );
  }
  // Route: embed via iframe. Users still get the full app inside the pane.
  if (tool.route) {
    return (
      <div className="w-full h-full min-h-[70vh] flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-slate-500 truncate">Route: {tool.route}</div>
          <a
            href={tool.route}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline flex items-center gap-1"
            data-testid={`ws-open-external-${tool.slug}`}
          >
            <ExternalLink className="w-3 h-3" /> Open full page
          </a>
        </div>
        <iframe
          src={tool.route}
          title={tool.name}
          className="w-full flex-1 rounded border border-border bg-background"
          style={{ minHeight: '65vh' }}
        />
      </div>
    );
  }
  return null;
}

// -------------------------------------------------------------------- Page
export default function AdminToolWorkstation() {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [openTools, setOpenTools] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [editing, setEditing] = useState(null);   // null | tool object | {id:null} for new
  const [feedbackTool, setFeedbackTool] = useState(null); // tool being viewed for feedback
  const [gridView, setGridView] = useState(true); // toggle between grid + workspace only

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/tools`);
      setTools(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load tools');
    } finally {
      setLoading(false);
    }
  };

  const openTool = (tool) => {
    setOpenTools((prev) => {
      if (prev.find((t) => t.id === tool.id)) return prev;
      return [...prev, tool];
    });
    setActiveId(tool.id);
    setGridView(false);
  };

  const closeTool = (id) => {
    setOpenTools((prev) => {
      const next = prev.filter((t) => t.id !== id);
      if (activeId === id) setActiveId(next[0]?.id || null);
      return next;
    });
  };

  const quickUpdate = async (tool, field, value) => {
    try {
      await axios.put(`${API}/admin/tools/${tool.id}`, { [field]: value });
      setTools((prev) => prev.map((t) => (t.id === tool.id ? { ...t, [field]: value } : t)));
      toast.success(`Updated ${tool.name}: ${field} → ${value}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const saveTool = async (form) => {
    try {
      if (editing?.id) {
        const { data } = await axios.put(`${API}/admin/tools/${editing.id}`, form);
        setTools((prev) => prev.map((t) => (t.id === editing.id ? { ...t, ...data } : t)));
        toast.success('Tool updated');
      } else {
        const { data } = await axios.post(`${API}/admin/tools`, form);
        setTools((prev) => [...prev, data]);
        toast.success('Tool created');
      }
      setEditing(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    }
  };

  const deleteTool = async (tool) => {
    if (!window.confirm(`Delete "${tool.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/tools/${tool.id}`);
      setTools((prev) => prev.filter((t) => t.id !== tool.id));
      closeTool(tool.id);
      toast.success('Tool deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const categories = useMemo(() => {
    const s = new Set(tools.map((t) => t.category || 'General'));
    return ['all', ...Array.from(s).sort()];
  }, [tools]);

  const filtered = useMemo(() => {
    const qq = q.toLowerCase().trim();
    return tools.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (categoryFilter !== 'all' && (t.category || 'General') !== categoryFilter) return false;
      if (qq && !`${t.name} ${t.description} ${t.slug} ${t.category}`.toLowerCase().includes(qq)) return false;
      return true;
    });
  }, [tools, q, statusFilter, categoryFilter]);

  const stats = useMemo(() => {
    const by = { concept: 0, wip: 0, beta: 0, live: 0 };
    for (const t of tools) by[t.status] = (by[t.status] || 0) + 1;
    return by;
  }, [tools]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="tool-workstation-root">
      {/* Header */}
      <div className="border-b border-border bg-card/40 backdrop-blur sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/portal')} data-testid="ws-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Portal
          </Button>
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-primary" />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Workstation</div>
              <div className="text-lg font-bold">Tool Development Platform</div>
            </div>
          </div>
          <div className="flex-1" />
          <div className="hidden md:flex items-center gap-2 text-[11px]">
            <Badge variant="outline">Live: {stats.live}</Badge>
            <Badge variant="outline">Beta: {stats.beta}</Badge>
            <Badge variant="outline">WIP: {stats.wip}</Badge>
            <Badge variant="outline">Concept: {stats.concept}</Badge>
          </div>
          <Button
            variant={gridView ? 'default' : 'outline'}
            size="sm"
            onClick={() => setGridView((v) => !v)}
            data-testid="ws-grid-toggle"
          >
            <LayoutGrid className="w-4 h-4 mr-1.5" /> {gridView ? 'Hide grid' : 'Show grid'}
          </Button>
          <Button size="sm" onClick={() => setEditing({})} data-testid="ws-new-tool">
            <Plus className="w-4 h-4 mr-1.5" /> New tool
          </Button>
        </div>
      </div>

      {/* Editing modal (inline card) */}
      {editing ? (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-start justify-center p-4 overflow-auto" onClick={() => setEditing(null)}>
          <div className="w-full max-w-3xl mt-16" onClick={(e) => e.stopPropagation()}>
            <ToolForm
              initial={editing}
              onSave={saveTool}
              onCancel={() => setEditing(null)}
            />
          </div>
        </div>
      ) : null}

      {/* Feedback drawer */}
      {feedbackTool ? (
        <ToolFeedbackDrawer
          tool={feedbackTool}
          onClose={() => setFeedbackTool(null)}
          onCountsChange={(counts) => {
            setTools((prev) => prev.map((t) => (t.id === feedbackTool.id ? { ...t, ...counts } : t)));
            setFeedbackTool((prev) => (prev ? { ...prev, ...counts } : prev));
          }}
        />
      ) : null}

      {/* Body */}
      <div className="max-w-[1600px] mx-auto p-4 grid gap-4" style={{
        gridTemplateColumns: gridView ? '1fr 1fr' : '360px 1fr',
      }}>
        {/* Grid pane */}
        <div className={gridView ? '' : 'hidden lg:block'}>
          {/* Filters */}
          <Card className="mb-3">
            <CardContent className="p-3 flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search tools…"
                  className="pl-8 h-9"
                  data-testid="ws-search"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-9 px-2 rounded border border-input bg-background text-sm"
                data-testid="ws-status-filter"
              >
                <option value="all">All statuses</option>
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                ))}
              </select>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="h-9 px-2 rounded border border-input bg-background text-sm"
                data-testid="ws-category-filter"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>{c === 'all' ? 'All categories' : c}</option>
                ))}
              </select>
            </CardContent>
          </Card>

          {loading ? (
            <div className="text-center p-10 text-slate-500 text-sm">Loading tools…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center p-10 text-slate-500 text-sm">No tools match those filters.</div>
          ) : (
            <div className={`grid gap-3 ${gridView ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
              {filtered.map((tool) => (
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  onOpen={openTool}
                  onEdit={setEditing}
                  onDelete={deleteTool}
                  onQuickStatus={quickUpdate}
                  onOpenFeedback={setFeedbackTool}
                />
              ))}
            </div>
          )}
        </div>

        {/* Workspace pane */}
        <Card className="min-h-[70vh]" data-testid="ws-panel">
          <ToolWorkspace
            openTools={openTools}
            activeId={activeId}
            onSelect={setActiveId}
            onClose={closeTool}
          />
        </Card>
      </div>
    </div>
  );
}
